<template>
  <div class="timepickers">
   <div class="timepicker timepicker--from">
     <div v-if="range" class="timepicker__label">
       {{ $t('front.from') }}
     </div>

     <div class="timepicker__body">
       <div class="timepicker__date">
         {{ dateFrom || '-' }}
       </div>

       <div class="timepicker__time">
         <FormControl
             type="timepicker"
             v-model="timeFrom"
             :disabled="!dateFrom"
             @change="onSelectFrom"
         />
       </div>
     </div>
   </div>

    <span v-if="range" />

    <div v-if="range"
         class="timepicker timepicker--to"
    >
      <div class="timepicker__label">
        {{ $t('front.to') }}
      </div>
      <div class="timepicker__body">
        <div class="timepicker__date">
          {{ dateTo || '-' }}
        </div>
        <div class="timepicker__time">
          <FormControl
              type="timepicker"
              v-model="timeTo"
              :disabled="!dateTo"
              @change="onSelectTo"
          />
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import FormControl from "@/js/Elements/Form/FormControl.vue";
import {computed, ref, watch} from "vue";
import moment from "moment";

const props = defineProps({
  time: [String, Object], // input value (changes only on apply)
  update: Function,
  range: Boolean,
  date: Array, // datepicker internal model value (changes on fly)
  dst: Boolean,
});

const emit = defineEmits(['select', 'selectFrom', 'selectTo'])
const timeFrom = ref(null);
const timeTo = ref(null);

setTime(props.date);
watch(() => props.date, setTime, { deep: props.range });

const dateFrom = computed(() => {
  const value = props.date[0];
  return value ? moment(value.split(' ')[0]).format(props.dst ? 'MM-DD' : 'L') : null;
});
const dateTo = computed(() => {
  const value = props.date[1];
  return value ? moment(value.split(' ')[0]).format(props.dst ? 'MM-DD' : 'L') : null;
});

function setTime(value) {
  timeFrom.value = getTime(value[0]);
  timeTo.value = props.range ? getTime(value[1]) : null;
}

function onSelectFrom(value) {
  if (typeof value.split !== 'function') return;

  value = value.split(':');
  emit('selectFrom', value);
  emit('select');
}

function onSelectTo(value) {
  if (typeof value.split !== 'function') return;

  value = value.split(':');
  emit('selectTo', value);
  emit('select');
}

function getTime(value) {
  if (!value) return null;
  return value.split(' ')[1];
}

function getHours(value) {
  if (!value) return null;
  return value.split(':')[0];
}

function getMinutes(value) {
  if (!value) return null;
  return value.split(':')[1];
}
</script>
<style lang="scss" scoped>
@import "@/css/functions";
@import "@/css/variables";
@import "@/css/mixins";
@import "bootstrap/scss/functions";
@import "bootstrap/scss/mixins";
@import "bootstrap/scss/variables";

.timepickers {
  display: flex;
  flex-direction: column;

  > span {
    display: none;
    width: toRem(20);
    height: toRem(1);
    background-color: themeColor('neutral-500');
    align-self: center;
    margin: toRem(19) spacing(2) 0 spacing(2);
  }

  @include media-breakpoint-up(sm) {
    flex-direction: row;

    > span {
      display: inline-flex;
    }
  }
}

.timepicker {
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: spacing(2) 0;

  &__label {
    @include fontLabel();
    font-weight: 700;
  }

  &__body {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: spacing(2);
  }

  &__date {
    width: 100%;
    max-width: toRem(120);
    height: toRem(32);
    border: themeColor('border') solid toRem(1);
    border-radius: $input-border-radius;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    font-size: fontSize(4);
    font-weight: $font-weight-medium;
    background-color: themeColor('neutral-100');
    cursor: default;
  }

  &__time {
    display: flex;
    width: toRem(90);
    max-width: toRem(90);

    &:deep(.form-control) {
      height: toRem(32);
      display: flex;
      align-items: center;
      padding-top: 0;
      padding-bottom: toRem(3);
      text-align: center;
      font-size: fontSize(5);
    }

    &:deep(.input-group-text) {
      padding: toRem(5);
    }
  }
}
</style>